import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { toast } from '@grandvisionhq/common';
import { Util } from '@grandvisionhq/forms';
import { useLink } from '@grandvisionhq/frk-routing';
import { pushReturnsEvent } from '../data-layer/returns.js';
import { createFieldIds } from '../utils/create-field-ids.js';
import { getReturnReason } from '../utils/get-return-reason.js';
import { useCreateReturn } from './use-create-return.js';

function getCheckboxKeys(formState) {
    return Object.keys(formState).filter((key)=>key.startsWith('item-checkbox')
    );
}
function isAnyCheckboxChecked(formState) {
    const checkboxKeys = getCheckboxKeys(formState);
    // always return true if it's only for one item
    if (checkboxKeys.length === 1) return true;
    return checkboxKeys.some((key)=>{
        var ref;
        return ((ref = formState[key]) === null || ref === void 0 ? void 0 : ref.value) !== '';
    });
}
function processFormState(formState) {
    const checkboxKeys = getCheckboxKeys(formState);
    return checkboxKeys.reduce((acc, key)=>{
        var ref6, ref1;
        const getDefaultFieldState = (fieldKey)=>{
            var ref, ref2, ref3;
            var ref4, ref5;
            return {
                isTouched: true,
                name: (ref4 = (ref = formState[fieldKey]) === null || ref === void 0 ? void 0 : ref.name) !== null && ref4 !== void 0 ? ref4 : '',
                value: (ref5 = (ref2 = formState[fieldKey]) === null || ref2 === void 0 ? void 0 : ref2.value) !== null && ref5 !== void 0 ? ref5 : '',
                isValid: !!((ref3 = formState[fieldKey]) === null || ref3 === void 0 ? void 0 : ref3.value)
            };
        };
        // Always include the checkbox, but update its required and valid status
        acc[key] = swcHelpers.objectSpread({}, getDefaultFieldState(key), {
            isValid: isAnyCheckboxChecked(formState) || !!((ref6 = formState[key]) === null || ref6 === void 0 ? void 0 : ref6.value)
        });
        // First check if it's about one item, then only add the reason key to validation
        if (getCheckboxKeys(formState).length === 1 || ((ref1 = formState[key]) === null || ref1 === void 0 ? void 0 : ref1.value) !== '') {
            const [index] = key.split('-').slice(2);
            const fieldIds = createFieldIds(index !== null && index !== void 0 ? index : '');
            acc[fieldIds.reason] = getDefaultFieldState(fieldIds.reason);
            acc[fieldIds.quantity] = swcHelpers.objectSpread({}, getDefaultFieldState(fieldIds.quantity), {
                isValid: true
            });
        }
        return acc;
    }, {});
}
// Create initial state with a checkbox for each line item
function getInitialState(lineItems) {
    return lineItems.map((_, index)=>{
        const fieldIds = createFieldIds(`${index}`);
        return {
            [fieldIds.checkbox]: {
                name: fieldIds.checkbox,
                value: '',
                isValid: false,
                isTouched: false
            }
        };
    }).reduce((acc, item)=>swcHelpers.objectSpread({}, acc, item)
    );
}
const decapitalizeFirstLetter = (str)=>str.charAt(0).toLowerCase() + str.slice(1)
;
const useReturnItemsForm = ({ email , orderNumber , lineItems , returnReasons ,  })=>{
    const { onClick  } = useLink({
        route: 'returns.confirmation',
        params: {
            email,
            orderNumber
        }
    }) || {};
    const onCompleted = (response)=>{
        if (response.createReturn.failures && response.createReturn.failures.length === 0) {
            onClick === null || onClick === void 0 ? void 0 : onClick();
        } else {
            const errorMessages = response.createReturn.failures.map((error)=>{
                if (error.__typename) {
                    return `returns.errors.${decapitalizeFirstLetter(error.__typename.replace('Failure', ''))}.message`;
                }
                return 'returns.returnGeneralFailure.message';
            });
            errorMessages.forEach((errorMessage)=>{
                toast.failed(errorMessage);
            });
        }
    };
    const onError = ()=>{
        toast.failed('returns.returnGeneralFailure.message');
    };
    const [trigger, { loading  }] = useCreateReturn({
        onCompleted,
        onError
    });
    const [formState1, setFormState] = React.useState(lineItems ? getInitialState(lineItems) : {});
    // Custom validation to be done in conjunction with form setting `showErrorsOnInvalidSubmit={false}`
    const validateFormFields = React.useCallback((formState)=>{
        const alteredFormState = processFormState(formState);
        if (!Util.getValidityFromState(alteredFormState) || !isAnyCheckboxChecked(alteredFormState)) {
            if (!isAnyCheckboxChecked(alteredFormState)) {
                // Show error message if no checkboxes have been checked
                toast.failed('returns.page.selectItems.form.error.noItemSelected');
            } else {
                // Show error message when the form has errors, only reasons are required and have no default value so we can be specific
                toast.failed('returns.page.selectItems.form.error.reasonRequired');
            }
            setFormState(alteredFormState);
            return false;
        }
        return true;
    }, []);
    const submitForm = React.useCallback(function() {
        var _ref = swcHelpers.asyncToGenerator(function*({ formState  }) {
            if (!lineItems) {
                console.error(`Can't submit returns form without line items for order ${orderNumber}`);
                return;
            }
            const formIsValid = validateFormFields(formState);
            if (!formIsValid) return;
            const values = Util.getValuesFromState(formState);
            const onlySelectedItems = (id)=>{
                const inputNames = createFieldIds(`${id}`);
                const checkbox = values[inputNames.checkbox];
                return lineItems.length > 1 ? checkbox === 'true' && formIsValid : formIsValid;
            };
            const inputItems = lineItems.map((lineItem, index)=>{
                const inputNames = createFieldIds(`${index}`);
                const reasonText = getReturnReason(returnReasons, inputNames, values);
                var _reason;
                return {
                    quantity: Number(values[inputNames.quantity]) || 1,
                    reason: (_reason = values[inputNames.reason]) !== null && _reason !== void 0 ? _reason : '',
                    reasonText: reasonText !== null && reasonText !== void 0 ? reasonText : '',
                    sku: lineItem.sku,
                    lineItemNumber: lineItem.lineItemNumber
                };
            }).filter((_, index)=>onlySelectedItems(index)
            );
            if (inputItems.length > 0) {
                var ref;
                pushReturnsEvent({
                    interactionType: 'confirm_return',
                    items: inputItems.map((item, index)=>{
                        var ref7;
                        return swcHelpers.objectSpread({}, item, {
                            name: (ref = (ref7 = lineItems[index]) === null || ref7 === void 0 ? void 0 : ref7.displayName) !== null && ref !== void 0 ? ref : ''
                        });
                    })
                });
                yield trigger({
                    email,
                    orderNumber,
                    lineItems: inputItems
                });
            }
        });
        return function(_) {
            return _ref.apply(this, arguments);
        };
    }(), [
        email,
        lineItems,
        orderNumber,
        trigger,
        validateFormFields
    ]);
    const triggers = React.useMemo(()=>({
            isAnyCheckboxChecked,
            submitForm
        })
    , [
        submitForm
    ]);
    const state = React.useMemo(()=>({
            formState: formState1,
            loading
        })
    , [
        formState1,
        loading
    ]);
    return React.useMemo(()=>[
            triggers,
            state
        ]
    , [
        triggers,
        state
    ]);
};

export { useReturnItemsForm };
//# sourceMappingURL=use-return-items-form.js.map
